// Podcast — bucket page grouping Episodes / Queue / Settings as sub-tabs.
// Same shape as Outreach / SettingsBucket. Tab state in ?tab=... so deep
// links survive.

import { useSearchParams, Link } from 'react-router-dom';
import { useState } from 'react';
import {
  usePodcastEpisodes,
  usePodcastEpisode,
  useUpdatePodcastEpisode,
  type PodcastEpisodeRow,
  type PodcastEpisodeStatus,
} from '../lib/queries';

type TabId = 'episodes' | 'queue' | 'settings';
const VALID: TabId[] = ['episodes', 'queue', 'settings'];
const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'episodes', label: 'Episodes' },
  { id: 'queue', label: 'Queue' },
  { id: 'settings', label: 'Settings' },
];

export function Podcast() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') as TabId | null;
  const tab: TabId = raw && VALID.includes(raw) ? raw : 'episodes';
  const episodeId = params.get('ep');

  const setTab = (id: TabId) => {
    params.set('tab', id);
    params.delete('ep');
    setParams(params, { replace: true });
  };

  return (
    <div className="bucket">
      <nav className="bucket__tabs" aria-label="Podcast views">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`bucket__tab ${tab === t.id ? 'bucket__tab--active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>
      <div className="bucket__body">
        {tab === 'episodes' && (episodeId ? <EpisodeDetail id={episodeId} /> : <EpisodesList />)}
        {tab === 'queue' && <QueueTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Episodes list

const STATUS_LABELS: Record<PodcastEpisodeStatus, string> = {
  researching: 'researching',
  ready_for_audio: 'ready for audio',
  audio_recorded: 'audio recorded',
  published: 'published',
  failed: 'failed',
};

function EpisodesList() {
  const { data, isLoading } = usePodcastEpisodes();
  const episodes = data ?? [];

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section E</span>
          <span>Podcast</span>
          <span>{episodes.length} episode{episodes.length === 1 ? '' : 's'}</span>
        </div>
        <h1 className="page__title">Stax Podcast — Episodes</h1>
        <p className="page__deck">
          Auto-generated weekly. Click an episode to download the NotebookLM-ready
          Markdown packet + source list, then paste your NotebookLM audio URL and
          Spotify URL back here when published.
        </p>
      </header>

      {isLoading && <p><em>Loading episodes…</em></p>}

      {!isLoading && episodes.length === 0 && (
        <div className="podcast__empty">
          No episodes yet. The first research packet generates on Sunday at 14:00 UTC.
        </div>
      )}

      {episodes.length > 0 && (
        <table className="podcast__table">
          <thead>
            <tr>
              <th>#</th>
              <th>Week of</th>
              <th>Topic</th>
              <th>Status</th>
              <th>NotebookLM</th>
              <th>Spotify</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {episodes.map((e) => (
              <EpisodeRow key={e.id} episode={e} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EpisodeRow({ episode }: { episode: PodcastEpisodeRow }) {
  return (
    <tr className="podcast__row">
      <td className="podcast__num">{episode.episode_number}</td>
      <td className="podcast__when">
        {new Date(episode.week_of).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
        })}
      </td>
      <td className="podcast__topic">
        {episode.topic ?? <span className="podcast__muted">—</span>}
      </td>
      <td>
        <span className={`podcast__status podcast__status--${episode.status}`}>
          {STATUS_LABELS[episode.status]}
        </span>
      </td>
      <td>
        {episode.notebooklm_url ? (
          <a href={episode.notebooklm_url} target="_blank" rel="noopener noreferrer" className="podcast__link">
            open →
          </a>
        ) : (
          <span className="podcast__muted">—</span>
        )}
      </td>
      <td>
        {episode.spotify_url ? (
          <a href={episode.spotify_url} target="_blank" rel="noopener noreferrer" className="podcast__link">
            open →
          </a>
        ) : (
          <span className="podcast__muted">—</span>
        )}
      </td>
      <td>
        <Link to={`/podcast?tab=episodes&ep=${episode.id}`} className="podcast__detail-btn">
          details →
        </Link>
      </td>
    </tr>
  );
}

// ----------------------------------------------------------------------------
// Episode detail — full markdown + manual URL inputs + status setter

function EpisodeDetail({ id }: { id: string }) {
  const { data: episode, isLoading } = usePodcastEpisode(id);
  const update = useUpdatePodcastEpisode(id);
  const [notebookUrl, setNotebookUrl] = useState('');
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [promptCopied, setPromptCopied] = useState(false);
  const [spotifyDesc, setSpotifyDesc] = useState('');
  const [descCopied, setDescCopied] = useState(false);

  if (isLoading) return <div className="page"><p><em>Loading…</em></p></div>;
  if (!episode) return <div className="page"><p>Episode not found.</p></div>;

  // Initialize inputs from episode the first time it loads.
  if (!notebookUrl && episode.notebooklm_url) setNotebookUrl(episode.notebooklm_url);
  if (!spotifyUrl && episode.spotify_url) setSpotifyUrl(episode.spotify_url);
  if (!customPrompt && episode.notebooklm_customization_prompt) {
    setCustomPrompt(episode.notebooklm_customization_prompt);
  }
  if (!spotifyDesc && episode.spotify_description) {
    setSpotifyDesc(episode.spotify_description);
  }

  // Build the .md download client-side from the already-loaded markdown.
  // Avoids two bugs in the previous <a href="/api/..."> approach:
  //   1. Relative URL resolved to the Pages host, not the Worker, so the
  //      SPA fallback returned index.html as the "markdown" file.
  //   2. Native <a download> can't attach the Bearer JWT, so even with the
  //      right host the Worker would 401.
  const downloadMarkdown = () => {
    if (!episode.research_markdown) return;
    const blob = new Blob([episode.research_markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stax-podcast-ep${episode.episode_number}-${episode.week_of}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section E · Podcast</span>
          <span>Episode {episode.episode_number}</span>
          <span>Week of {new Date(episode.week_of).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
        <h1 className="page__title">{episode.topic ?? '(untitled)'}</h1>
        {episode.angle && <p className="page__deck">{episode.angle}</p>}
        <div className="podcast__status-row">
          <span className={`podcast__status podcast__status--${episode.status}`}>
            {STATUS_LABELS[episode.status]}
          </span>
          <Link to="/podcast?tab=episodes" className="podcast__back-link">← back to episodes</Link>
        </div>
      </header>

      <section className="podcast__detail-grid">
        <div className="podcast__detail-main">
          <h2 className="podcast__section-title">Research packet</h2>
          <div className="podcast__actions">
            <button
              type="button"
              className="podcast__action-btn podcast__action-btn--primary"
              onClick={downloadMarkdown}
              disabled={!episode.research_markdown}
            >
              ⬇ Download Markdown (for NotebookLM)
            </button>
            <button
              type="button"
              className="podcast__action-btn"
              onClick={() => {
                if (episode.research_markdown) {
                  navigator.clipboard.writeText(episode.research_markdown);
                }
              }}
            >
              📋 Copy raw text
            </button>
          </div>
          <pre className="podcast__markdown">
            {episode.research_markdown ?? '(no markdown stored)'}
          </pre>
        </div>

        <aside className="podcast__detail-side">
          <section className="podcast__side-block">
            <h3 className="podcast__side-title">Workflow status</h3>
            <div className="podcast__status-stepper">
              <StatusStep label="Research" active complete />
              <StatusStep
                label="Audio (NotebookLM)"
                active={episode.status === 'ready_for_audio' || episode.status === 'audio_recorded' || episode.status === 'published'}
                complete={Boolean(episode.notebooklm_url)}
              />
              <StatusStep
                label="Published (Spotify)"
                active={episode.status === 'audio_recorded' || episode.status === 'published'}
                complete={Boolean(episode.spotify_url)}
              />
            </div>
          </section>

          <section className="podcast__side-block">
            <h3 className="podcast__side-title">NotebookLM URL</h3>
            <input
              type="url"
              className="podcast__input"
              placeholder="https://notebooklm.google.com/notebook/..."
              value={notebookUrl}
              onChange={(e) => setNotebookUrl(e.target.value)}
            />
            <button
              type="button"
              className="podcast__action-btn"
              disabled={update.isPending || notebookUrl === (episode.notebooklm_url ?? '')}
              onClick={() =>
                update.mutate({
                  notebooklm_url: notebookUrl,
                  status: notebookUrl ? 'audio_recorded' : episode.status,
                })
              }
            >
              {update.isPending ? 'Saving…' : 'Save NotebookLM URL'}
            </button>
          </section>

          <section className="podcast__side-block">
            <h3 className="podcast__side-title">Spotify URL</h3>
            <input
              type="url"
              className="podcast__input"
              placeholder="https://open.spotify.com/episode/..."
              value={spotifyUrl}
              onChange={(e) => setSpotifyUrl(e.target.value)}
            />
            <button
              type="button"
              className="podcast__action-btn"
              disabled={update.isPending || spotifyUrl === (episode.spotify_url ?? '')}
              onClick={() =>
                update.mutate({
                  spotify_url: spotifyUrl,
                  status: spotifyUrl ? 'published' : episode.status,
                })
              }
            >
              {update.isPending ? 'Saving…' : 'Save Spotify URL & mark published'}
            </button>
          </section>

          <section className="podcast__side-block">
            <h3 className="podcast__side-title">Spotify episode description</h3>
            <p className="podcast__side-help">
              Auto-written for this episode. Paste it under the episode on Spotify.
              Edit freely before publishing.
            </p>
            <textarea
              className="podcast__textarea"
              rows={5}
              value={spotifyDesc}
              onChange={(e) => setSpotifyDesc(e.target.value)}
              placeholder="(no description generated)"
            />
            <div className="podcast__prompt-actions">
              <button
                type="button"
                className="podcast__action-btn podcast__action-btn--primary"
                disabled={!spotifyDesc}
                onClick={() => {
                  navigator.clipboard.writeText(spotifyDesc);
                  setDescCopied(true);
                  setTimeout(() => setDescCopied(false), 1800);
                }}
              >
                {descCopied ? '✓ Copied' : '📋 Copy description'}
              </button>
              <button
                type="button"
                className="podcast__action-btn"
                disabled={update.isPending || spotifyDesc === (episode.spotify_description ?? '')}
                onClick={() => update.mutate({ spotify_description: spotifyDesc })}
              >
                {update.isPending ? 'Saving…' : 'Save edits'}
              </button>
            </div>
          </section>

          <section className="podcast__side-block">
            <h3 className="podcast__side-title">NotebookLM voice prompt</h3>
            <p className="podcast__side-help">
              Paste this into NotebookLM's <strong>Customize</strong> box when
              generating Audio Overview. Controls how human the AI hosts sound.
            </p>
            <textarea
              className="podcast__textarea"
              rows={6}
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
            />
            <div className="podcast__prompt-actions">
              <button
                type="button"
                className="podcast__action-btn podcast__action-btn--primary"
                onClick={() => {
                  navigator.clipboard.writeText(customPrompt);
                  setPromptCopied(true);
                  setTimeout(() => setPromptCopied(false), 1800);
                }}
              >
                {promptCopied ? '✓ Copied' : '📋 Copy prompt'}
              </button>
              <button
                type="button"
                className="podcast__action-btn"
                disabled={update.isPending || customPrompt === episode.notebooklm_customization_prompt}
                onClick={() =>
                  update.mutate({ notebooklm_customization_prompt: customPrompt })
                }
              >
                {update.isPending ? 'Saving…' : 'Save edits'}
              </button>
            </div>
          </section>

          <section className="podcast__side-block">
            <h3 className="podcast__side-title">Sources ({episode.source_urls.length})</h3>
            <ul className="podcast__source-list">
              {episode.source_urls.map((u) => (
                <li key={u}>
                  <a href={u} target="_blank" rel="noopener noreferrer" className="podcast__source-link">
                    {u}
                  </a>
                </li>
              ))}
            </ul>
          </section>

          {episode.stax_connection && (
            <section className="podcast__side-block">
              <h3 className="podcast__side-title">Stax angle</h3>
              <p className="podcast__side-text">{episode.stax_connection}</p>
            </section>
          )}
        </aside>
      </section>
    </div>
  );
}

function StatusStep({ label, active, complete }: { label: string; active?: boolean; complete?: boolean }) {
  return (
    <div className={`podcast__step ${active ? 'podcast__step--active' : ''} ${complete ? 'podcast__step--complete' : ''}`}>
      <span className="podcast__step-dot">{complete ? '✓' : '○'}</span>
      <span className="podcast__step-label">{label}</span>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Queue tab — read-only preview of what the picker would surface next.
// MVP: just shows the picker schedule + theme set. Adding a live preview of
// next week's candidate scoring is a future-iteration nice-to-have.

function QueueTab() {
  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section E · Podcast</span>
          <span>Queue</span>
          <span>Next: Sunday 14:00 UTC</span>
        </div>
        <h1 className="page__title">Queue</h1>
        <p className="page__deck">
          The research engine fires automatically every Sunday at 14:00 UTC (10 AM ET).
          It pulls from Google News finance feeds, arXiv q-fin papers, FMP market
          snapshot, and the Stax theme set below — then scores candidates and picks #1.
        </p>
      </header>
      <section className="podcast__themes">
        <h2 className="podcast__section-title">Stax theme set</h2>
        <p className="podcast__deck-muted">
          These themes anchor topic picking when news is quiet. The picker boosts
          candidates whose headlines/abstracts match these keywords, and penalizes
          themes covered in the last 4 episodes for diversity.
        </p>
        <ul className="podcast__theme-list">
          {STAX_THEMES_DISPLAY.map((t) => (
            <li key={t.label} className="podcast__theme-item">
              <strong>{t.label}</strong>
              <div className="podcast__theme-kw">{t.keywords.join(' · ')}</div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

// Mirror of STAX_THEMES in the worker — kept in this file (not imported)
// so the dashboard works without coupling to a worker package.
const STAX_THEMES_DISPLAY = [
  { label: 'Algorithmic & systematic investing', keywords: ['algorithm', 'systematic', 'quant', 'rules-based', 'automated trading'] },
  { label: 'Factor investing & smart beta', keywords: ['factor', 'smart beta', 'fama-french', 'momentum', 'value factor', 'quality factor'] },
  { label: 'Backtesting & in-sample bias', keywords: ['backtest', 'walk-forward', 'overfitting', 'in-sample', 'out-of-sample'] },
  { label: 'Risk-adjusted returns & drawdowns', keywords: ['sharpe', 'sortino', 'drawdown', 'risk parity', 'tail risk'] },
  { label: 'Momentum & trend following', keywords: ['momentum', 'trend following', 'breakout', 'price momentum'] },
  { label: 'Mean reversion & contrarian', keywords: ['mean reversion', 'mean-reverting', 'contrarian', 'pairs trading'] },
  { label: 'Macro signals & regime detection', keywords: ['regime', 'macro', 'cpi', 'fed', 'inflation', 'yield curve'] },
  { label: 'Behavioral finance', keywords: ['behavioral', 'psychology', 'emotion', 'cognitive bias'] },
  { label: 'Options & volatility', keywords: ['volatility', 'options', 'covered call', 'gamma', 'vix'] },
  { label: 'Crypto & alternative assets', keywords: ['bitcoin', 'crypto', 'digital asset', 'tokenization'] },
];

// ----------------------------------------------------------------------------
// Settings tab — placeholder for future picker config

function SettingsTab() {
  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section E · Podcast</span>
          <span>Settings</span>
        </div>
        <h1 className="page__title">Settings</h1>
        <p className="page__deck">Picker config + theme management coming soon.</p>
      </header>
      <p className="podcast__deck-muted">
        The Stax theme keywords + news source URLs are currently hard-coded in
        <code> app/apps/worker/src/services/podcast-research.ts</code>. To adjust,
        edit that file and redeploy. UI controls land here in a later iteration.
      </p>
    </div>
  );
}
